import { useState } from 'react';
import { Block, Button, Navbar, NavRight, Page, Popup, Preloader } from 'framework7-react';
import type { SettingChange } from '@restaurant/api-client';
import { useT } from '@/i18n';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { formatMoney } from '@/utils/money';
import { useSettingsDetailBackLink } from '@/utils/useMediaQuery';
import { useSettingChanges } from '../hooks/useSettingChanges';
import { formatStructuredValue, visibleChange } from '../lib/format-setting-value';

const pad = (n: number) => String(n).padStart(2, '0');

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const MINUTE_FIELDS = new Set([
  'duration',
  'bufferTime',
  'cutoffTime',
  'slotInterval',
  'closingBufferMinutes',
  'gracePeriod',
  'defaultDurationMinutes',
  'deliveryBufferMinutes',
  'completeStaleAfterMinutesFromReadyAt',
  'expireUnpaidAfterMinutes',
  'prepTimeMinutes',
  'schedulingLeadMinutes',
  'openingBufferMinutes',
  'deliveryMinutes',
]);
const HOUR_FIELDS = new Set(['cancelPendingAfterHours']);
const DAY_FIELDS = new Set(['advanceWindow']);
const PEOPLE_FIELDS = new Set(['maxPartySize', 'seats']);
const MONEY_FIELDS = new Set(['minOrderCents', 'feeCents']);
const DISTANCE_FIELDS = new Set(['maxDistanceKm']);

const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/**
 * The page numbers to render: always the first, the last, and a window around
 * the current one, with `null` standing in for an elided run. Keeps the pager a
 * fixed width no matter how deep the trail goes.
 */
function pageNumbers(current: number, total: number): Array<number | null> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const window = new Set([1, total, current, current - 1, current + 1]);
  const pages = [...window].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);

  const out: Array<number | null> = [];
  pages.forEach((p, i) => {
    if (i > 0 && p - pages[i - 1] > 1) out.push(null);
    out.push(p);
  });
  return out;
}

function SettingHistoryPage() {
  const t = useT();
  const backLink = useSettingsDetailBackLink();

  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState<SettingChange | null>(null);
  const [showRaw, setShowRaw] = useState(false);
  const currency = useRestaurantStore((s) => s.restaurantConfig?.currency) ?? 'EUR';

  const { data, isLoading, isError, error, isFetching } = useSettingChanges({}, page);

  const rows = data?.items ?? [];
  const pageCount = data?.pageCount ?? 0;

  const actionLabel = (row: SettingChange) => t.settingHistory.actions[row.changeType];

  const fieldLabel = (field: string) => t.settingHistory.fields[field] ?? field;

  const formatValue = (field: string, raw: string | null) => {
    if (raw === null || raw === '') return '—';

    const structured = formatStructuredValue(field, raw, t);
    if (structured !== null) return structured;

    if (raw === 'true') return t.settingHistory.on;
    if (raw === 'false') return t.settingHistory.off;

    const known = t.settingHistory.values[raw];
    if (known) return known;

    if (ISO_INSTANT.test(raw)) return formatTimestamp(raw);
    if (MONEY_FIELDS.has(field)) return formatMoney(Number(raw), currency);
    if (MINUTE_FIELDS.has(field)) return t.settingHistory.unitMinutes(raw);
    if (HOUR_FIELDS.has(field)) return t.settingHistory.unitHours(raw);
    if (DAY_FIELDS.has(field)) return t.settingHistory.unitDays(raw);
    if (PEOPLE_FIELDS.has(field)) return t.settingHistory.unitPeople(raw);
    if (DISTANCE_FIELDS.has(field)) return t.settingHistory.unitKm(raw);

    return raw;
  };

  const targetText = (row: SettingChange) =>
    row.targetLabel
      ?.split(' · ')
      .map((part) => t.settingHistory.values[part] ?? part)
      .join(' · ');

  const changeText = (row: SettingChange, field: string): [string, string] => {
    if (row.isSensitive) return [t.settingHistory.valueHidden, t.settingHistory.valueHidden];
    return visibleChange(
      row.oldValue,
      row.newValue,
      formatValue(field, row.oldValue),
      formatValue(field, row.newValue),
    );
  };

  const summarize = (row: SettingChange) => {
    const target = targetText(row);
    const prefix = target ? `${target} · ` : '';
    if (!row.field) return prefix || '—';
    const label = `${prefix}${fieldLabel(row.field)}`;
    if (row.isSensitive) return `${label}: ${t.settingHistory.valueHidden}`;
    const [from, to] = changeText(row, row.field);
    return `${label}: ${from} → ${to}`;
  };

  const detailChange = detail?.field ? changeText(detail, detail.field) : null;

  return (
    <Page name="setting-history">
      <Navbar title={t.settingHistory.title} backLink={backLink} />

      {isLoading && (
        <Block className="text-align-center">
          <Preloader />
        </Block>
      )}

      {isError && (
        <Block strong inset>
          {error instanceof Error ? error.message : t.error}
        </Block>
      )}

      {!isLoading && !isError && rows.length === 0 && (
        <Block strong inset className="text-align-center">
          {t.settingHistory.empty}
        </Block>
      )}

      {rows.length > 0 && (
        <Block strong inset className={`data-table${isFetching ? ' setting-history-loading' : ''}`}>
          <div className="data-table-content">
            <table>
              <thead>
                <tr>
                  <th className="label-cell">{t.settingHistory.columnActor}</th>
                  <th className="label-cell">{t.settingHistory.columnWhen}</th>
                  <th className="label-cell">{t.settingHistory.columnAction}</th>
                  <th className="label-cell">{t.settingHistory.columnWhat}</th>
                  <th className="actions-cell" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="label-cell">{row.actorName}</td>
                    <td className="label-cell">{formatTimestamp(row.changedAt)}</td>
                    <td className="label-cell">{actionLabel(row)}</td>
                    <td className="label-cell setting-history-summary">{summarize(row)}</td>
                    <td className="actions-cell">
                      <Button small onClick={() => setDetail(row)}>
                        {t.settingHistory.details}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Block>
      )}

      {pageCount > 1 && (
        <Block className="setting-history-pager">
          <Button small round outline disabled={page <= 1} onClick={() => setPage(page - 1)}>
            ‹
          </Button>
          {pageNumbers(page, pageCount).map((p, i) =>
            p === null ? (
              <span key={`gap-${i}`} className="setting-history-gap">
                …
              </span>
            ) : (
              <Button
                key={p}
                small
                round
                fill={p === page}
                outline={p !== page}
                onClick={() => setPage(p)}
              >
                {p}
              </Button>
            ),
          )}
          <Button
            small
            round
            outline
            disabled={page >= pageCount}
            onClick={() => setPage(page + 1)}
          >
            ›
          </Button>
        </Block>
      )}

      {data && (
        <Block className="setting-history-total">{t.settingHistory.totalEntries(data.total)}</Block>
      )}

      <Popup
        opened={detail !== null}
        onPopupClosed={() => {
          setDetail(null);
          setShowRaw(false);
        }}
      >
        <Page>
          <Navbar title={t.settingHistory.detailsTitle}>
            <NavRight>
              <Button popupClose>{t.close}</Button>
            </NavRight>
          </Navbar>
          {detail && (
            <Block strong inset>
              <dl className="setting-history-detail">
                <div>
                  <dt>{t.settingHistory.columnWhat}</dt>
                  <dd>
                    {[targetText(detail), detail.field && fieldLabel(detail.field)]
                      .filter(Boolean)
                      .join(' · ') || '—'}
                  </dd>
                </div>
                <div>
                  <dt>{t.settingHistory.columnAction}</dt>
                  <dd>{actionLabel(detail)}</dd>
                </div>
                {detailChange && (
                  <>
                    <div>
                      <dt>{t.settingHistory.detailBefore}</dt>
                      <dd>{detailChange[0]}</dd>
                    </div>
                    <div>
                      <dt>{t.settingHistory.detailAfter}</dt>
                      <dd>{detailChange[1]}</dd>
                    </div>
                  </>
                )}
                <div>
                  <dt>{t.settingHistory.columnActor}</dt>
                  <dd>{detail.actorName}</dd>
                </div>
                <div>
                  <dt>{t.settingHistory.columnWhen}</dt>
                  <dd>{formatTimestamp(detail.changedAt)}</dd>
                </div>
              </dl>
            </Block>
          )}
          <Block>
            <Button small outline onClick={() => setShowRaw(!showRaw)}>
              {showRaw ? t.settingHistory.hideRaw : t.settingHistory.showRaw}
            </Button>
          </Block>
          {/* The raw row. Everything the trail stored about this one change, for
              when the one-line summary isn't enough to settle an argument. */}
          {showRaw && (
            <Block strong inset>
              <pre className="setting-history-json">{JSON.stringify(detail, null, 2)}</pre>
            </Block>
          )}
        </Page>
      </Popup>
    </Page>
  );
}

export default SettingHistoryPage;
